import { PartnerDetailItemBar } from "@/components/bar/PartnerDetailItemBar";
import { PartnerDetailPageModalPanel } from "@/components/panel/PartnerDetailPageModalPanel";
import { PartnerDetailOrderTable } from "@/components/table/partner-detail-order-table/PartnerDetailOrderTable";
import { PartnerDetailQuoteTable } from "@/components/table/partner-detail-quote-table/PartnerDetailQuoteTable";
import { PartnerItemTable } from "@/components/table/partner-item-table/PartnerItemTable";
import { H5 } from "@/components/typography/H5";
import { userService } from "@/services/user-service";
import { GradeDto } from "@/types/dto/grade-dto";
import { UserDto } from "@/types/dto/user-dto";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { PartnerIncentivesModal } from "@/components/modal/PartnerIncentivesModal";
import { DiscountDto } from "@/types/dto/discount-dto";
import { partnerProductOrderItemPageState } from "@/atoms/item/partner-product-order-item-page-state";
import { OrderItemSortType } from "@/common/enum/order-item-sort";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { itemService } from "@/services/item-service";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { PartnerDetailHeader } from "@/components/header/PartnerDetailHeader";
import { PartnerDetailTable } from "./Table";
import { PartnerHistoryModal } from "@/components/modal/PartnerHistoryModal";
import { PartnerAddressChangeModal } from "@/components/modal/PartnerAddressChangeModal";

export default function PartnerDetailPage() {
  const { id } = useParams();

  const [user, setUser] = useState<UserDto | undefined>(undefined);

  const [discounts, setDiscounts] = useState<DiscountDto[]>([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false); // 수정이력
  const [isIncentiveModalOpen, setIsIncentiveModalOpen] = useState(false); // 인센티브 할인 모달
  const [selectedGrade, setSelectedGrade] = useState<GradeDto | undefined>(); // 파트너 등급
  const [addressChangeModal, setAddressChangeModal] = useState(false);

  // 파트너 상세 정보 수정
  // PartnerDetailTable에 넘겨줘서 표현
  const [partnerInfo, setPartnerInfo] = useState({
    partnerName: "",
    businessName: "",
    email: "",
    phone: "",
    createdAt: "",
  });

  const openIncentivesModal = () => {
    setIsIncentiveModalOpen(true);
  }; // 인센티브모달 오픈

  const closeIncentiveModal = () => {
    setIsIncentiveModalOpen(false);
  }; // 인센티브 모달 클로즈

  const openHistoryModal = () => {
    setIsHistoryModalOpen(true);
  }; // 수정이력 창 오픈

  const closeHistoryModal = () => {
    setIsHistoryModalOpen(false);
  }; // 수정이력 창 클로즈

  const [searchItemName, setSearchItemName] = useState<string>(""); // 검색을 위한 상태

  const [page, setPage] = useRecoilState<BasePageDto<ItemDetailDto>>(
    partnerProductOrderItemPageState
  ); // 리코일 통한 아이템 가져오기

  const [categoryIds, setCategoryIds] = useState<number[]>([]); // 카테고리 검색을 위한

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchItemName(event.target.value);
  }; // 검색

  const handleCategoryChange = (categoryIds: number[]) => {
    setCategoryIds(categoryIds);
  }; // 카테고리

  const handleSelectedGradeChange = (grade: GradeDto) => {
    setSelectedGrade(grade);
  }; // 등급 체인지

  // 해당 유저의 상품을 가져옴
  const getPage = async (name: string) => {
    if (!user) {
      return;
    }
    try {
      const response = await itemService.getPagePartner(user.id, {
        page: page.currentPage,
        size: 1000,
        orderType: OrderItemSortType.ALL,
        name,
        categoryIds,
      });

      setPage(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const [checkedItemIds, setCheckedItemIds] = useState<number[]>([]); // 체크를 위한

  const addCheckedItemId = (id: number) => {
    if (checkedItemIds.includes(id)) {
      return;
    }
    setCheckedItemIds([...checkedItemIds, id]);
  };
  const checkAllItemIds = () => {
    if (checkedItemIds.length === page.contents.length) {
      setCheckedItemIds([]);
      return;
    }
    setCheckedItemIds(page.contents.map((item: any) => item.id));
  };

  const removeCheckedItemId = (id: number) => {
    setCheckedItemIds(checkedItemIds.filter((checkedId) => checkedId !== id));
  };

  const resetCheckedItems = () => {
    setCheckedItemIds([]);
  };

  // 유저, 서치 아이템, 카테고리, 현재페이지가 바뀌면
  // 서치 아이템으로 getPage
  useEffect(() => {
    getPage(searchItemName);
  }, [user, searchItemName, categoryIds, page.currentPage]);

  const getUser = async () => {
    try {
      const response = await userService.getPartnerById(Number(id));
      if (response.ok) {
        setUser(response.data);
        setSelectedGrade(response.data.grade);
        setPartnerInfo({
          partnerName: response.data.name,
          businessName: response.data.businessName,
          email: response.data.email,
          phone: response.data.phone,
          createdAt: response.data.createdAt.toString(),
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getUser();
  }, [id]);

  useEffect(() => {
    const fetch = async () => {
      if (!user?.id) return;
      const response = await userService.getUserDiscount(user?.id);
      if (response.ok) {
        setDiscounts(response.data);
      }
    };
    fetch();
  }, [user]);

  if (!user) {
    return null;
  }

  if (!selectedGrade) {
    return null;
  }

  const handleAddressChangeModal = () => {
    setAddressChangeModal(!addressChangeModal);
  };

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px] h-full items-between justify-start gap-[16px]">
        <PartnerDetailHeader
          partnerInfo={partnerInfo}
          user={user}
          selectedGrade={selectedGrade}
          getUser={getUser}
          openHistoryModal={openHistoryModal}
        />
        <PartnerDetailTable
          user={user}
          setUser={setUser}
          selectedGrade={selectedGrade}
          handleSelectedGradeChange={handleSelectedGradeChange}
          partnerInfo={partnerInfo}
          setPartnerInfo={setPartnerInfo}
          handleAddressChangeModal={handleAddressChangeModal}
        />
        <div className="py-[8px]">
          <H5>매대 정보</H5>
        </div>
        <div className="flex flex-col">
          <PartnerDetailItemBar
            totalElements={page.totalElements}
            searchItemName={searchItemName}
            handleSearchChange={handleSearchChange}
            handleCategoryChange={handleCategoryChange}
          />
          <PartnerItemTable
            user={user}
            searchItemName={searchItemName}
            items={page.contents}
            checkedItemIds={checkedItemIds}
            gradeDiscount={user?.grade.discountRate || 0}
            addCheckedItemId={addCheckedItemId}
            checkAllItemIds={checkAllItemIds}
            removeCheckedItemId={removeCheckedItemId}
            openIncentivesModal={openIncentivesModal}
            getPage={getPage}
            discounts={discounts}
          />
        </div>
        <div className="py-[8px]">
          <H5>진행한 발주 내역</H5>
        </div>
        {<PartnerDetailOrderTable partnerId={user.id} />}
        <div className="py-[8px]">
          <H5>진행한 견적 내역</H5>
        </div>
        {<PartnerDetailQuoteTable partnerId={user.id} />}
      </div>
      {isHistoryModalOpen && (
        <PartnerHistoryModal
          partnerId={user.id}
          closeModal={closeHistoryModal}
        />
      )}
      {isIncentiveModalOpen && (
        <PartnerIncentivesModal
          user={user}
          checkedItemIds={checkedItemIds}
          searchItemName={searchItemName}
          getPage={getPage}
          closeModal={closeIncentiveModal}
          setDiscounts={setDiscounts}
          resetCheckedItems={resetCheckedItems}
        />
      )}
      <PartnerDetailPageModalPanel />
      {addressChangeModal && (
        <PartnerAddressChangeModal
          handleAddressChangeModal={handleAddressChangeModal}
          user={user}
          setUser={setUser}
          partnerId={user.id}
        />
      )}
    </PageWrapper>
  );
}
